"use client";
import { motion } from "framer-motion";
import { Boxes, Code2, Gamepad2, Send } from "lucide-react";
import { MEDIA, BRAND } from "../../lib/brand";

export default function About() {
  return (
    <section id="about" className="relative py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Left: text */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7"
          >
            <p
              className="text-xs uppercase tracking-[0.22em] text-[#00E5FF] mb-5"
              data-testid="about-label"
            >
              About Axion Bots
            </p>
            <h2
              className="font-display text-4xl md:text-5xl font-light tracking-tight leading-[1.08]"
              data-testid="about-heading"
            >
              A studio for{" "}
              <span className="gradient-text font-medium">premium Telegram bots</span>{" "}
              & automation.
            </h2>
            <p className="mt-6 text-neutral-400 text-base md:text-lg leading-relaxed max-w-2xl">
              Axion Bots is a Telegram bot development project focused on creating
              powerful, user-friendly automation tools and interactive bots for Telegram
              communities. We blend product craft with platform expertise to ship
              experiences your members actually love.
            </p>
            <p className="mt-4 text-neutral-500 text-base leading-relaxed max-w-2xl">
              Our flagship — <span className="text-white">Word Grid</span> —
              delivers engaging word games, community interaction features and
              automated utilities trusted across thousands of Telegram groups.
            </p>

            {/* Pillars */}
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { icon: Code2, t: "Custom bot engineering", d: "Tailor-made flows" },
                { icon: Gamepad2, t: "Game & engagement", d: "Built for fun" },
                { icon: Boxes, t: "Scalable infra", d: "99.98% uptime" },
              ].map((p, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1, duration: 0.5 }}
                  className="glass rounded-2xl p-5"
                  data-testid={`about-pillar-${i}`}
                >
                  <p.icon className="h-5 w-5 text-[#00E5FF]" />
                  <p className="mt-3 text-sm font-medium">{p.t}</p>
                  <p className="text-xs text-neutral-500 mt-1">{p.d}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right: glassy visual */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-5 relative"
          >
            <div className="relative aspect-[4/5] rounded-[28px] overflow-hidden border border-white/10 glow-soft">
              <img
                src={MEDIA.abstract_glow}
                alt="Abstract Tech Glow"
                className="absolute inset-0 w-full h-full object-cover"
                data-testid="about-image"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/40 via-[#050505]/30 to-[#050505]/90" />

              {/* Floating brand pill — top */}
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                className="absolute top-5 left-5 flex items-center gap-3 glass rounded-2xl px-3.5 py-2.5"
                data-testid="about-float-bot"
              >
                <div className="relative h-9 w-9 rounded-xl overflow-hidden border border-white/15">
                  <img src={MEDIA.axion_logo} alt="Axion Bots" className="h-full w-full object-cover" />
                </div>
                <div>
                  <p className="text-[11px] font-medium leading-none">Axion Bots</p>
                  <p className="text-[10px] text-neutral-400 mt-1 flex items-center gap-1">
                    <span className="h-1 w-1 rounded-full bg-emerald-400 inline-block" />
                    online · all systems go
                  </p>
                </div>
              </motion.div>

              {/* Chat preview card — middle */}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3, duration: 0.7 }}
                className="absolute left-5 right-5 top-[28%] glass rounded-2xl p-3.5 shadow-2xl shadow-black/40"
                data-testid="about-chat-card"
              >
                <div className="flex items-start gap-2.5">
                  <div className="h-8 w-8 shrink-0 rounded-xl bg-gradient-to-br from-[#007AFF] to-[#00E5FF] grid place-items-center">
                    <Send className="h-3.5 w-3.5 text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[11px] text-neutral-400 leading-none">{BRAND.axionHandle}</p>
                    <p className="mt-1.5 text-sm text-white leading-snug">
                      🚀 New automation deployed · 2k+ users today.
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* AXION letter strip — right side */}
              <motion.div
                animate={{ y: [0, 8, 0] }}
                transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
                className="absolute right-5 top-[52%] glass rounded-2xl p-3"
                data-testid="about-grid-mock"
              >
                <div className="flex gap-1.5">
                  {["A", "X", "I", "O", "N"].map((ch, i) => (
                    <div
                      key={i}
                      className={`h-7 w-7 rounded-md grid place-items-center text-[11px] font-bold ${
                        [0, 2, 4].includes(i)
                          ? "bg-[#229ED9] text-white"
                          : "bg-white/[0.06] text-neutral-200 border border-white/10"
                      }`}
                    >
                      {ch}
                    </div>
                  ))}
                </div>
                <p className="mt-2 text-center text-[9px] uppercase tracking-wider text-neutral-500">
                  Bots Active · Auto-scaling
                </p>
              </motion.div>

              {/* Stat overlay */}
              <div className="absolute left-5 right-5 bottom-5 glass rounded-2xl p-5" data-testid="about-stats-card">
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div>
                    <p className="font-display text-2xl font-medium">115K+</p>
                    <p className="text-[10px] uppercase tracking-wider text-neutral-500 mt-1">Users</p>
                  </div>
                  <div>
                    <p className="font-display text-2xl font-medium">7K+</p>
                    <p className="text-[10px] uppercase tracking-wider text-neutral-500 mt-1">Groups</p>
                  </div>
                  <div>
                    <p className="font-display text-2xl font-medium">2.2M+</p>
                    <p className="text-[10px] uppercase tracking-wider text-neutral-500 mt-1">Games</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
